// review-item-bodies — the rich per-kind review renderers.
//
// Extracted from the former ReviewQueueView page so they can be hosted both
// inline and inside the Kanban's <ReviewItemPanel> drawer. Each body owns the
// approve / reject / request-revision dispatch for its item kind:
//
//   - CallDraftsBody      → /api/agent/reviewables/call-* (+ "approve all")
//   - MonthlyNarrativeBody → /api/agent/reviewables/monthly-narrative-draft
//   - ComposerDraftBody    → the per-agent composition workbench (owns its own
//                            approve / reject / request-revision)
//   - TaskBody             → AgentContext.reviewTask (PATCH /api/agent/tasks/:id/approve|reject)
//
// Adding a new artefact type? Extend `useReviewQueue` with the item shape and
// add a renderer here.

import type {
    AgentRowTable,
    AgentTask,
    ContextTranscriptLine,
    ReviewableKind,
    ReviewDecision,
} from '@septima/contract';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
    AlertTriangle,
    CheckCircle2,
    FileText,
    Quote,
    RotateCcw,
    ThumbsDown,
    ThumbsUp,
    XCircle,
} from 'lucide-react';
import { Pill, Button } from '@/components/ui';
import EmptyState from '@/components/EmptyState';
import { useAgent } from '@/contexts/AgentContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
    approveReviewable,
    rejectReviewable,
    requestRevisionReviewable,
    submitReviewDecisions,
} from '@/lib/agent-reviewables';
import { useToast } from '@/lib/useToast';
import { cn } from '@/lib/utils';
import {
    summariseArtefact,
    type CallDraftsReviewItem,
    type CoacActionDraftReviewItem,
    type ComposerDraftReviewItem,
    type MonthlyNarrativeReviewItem,
    type TaskReviewItem,
} from '@/lib/use-review-queue';
import type { BaseDraft } from '@/data/types';
import CitedSummary from '../CitedSummary';
import {
    ContextBlocks,
    ContextSection,
    CitedTranscriptExpander,
    transcriptFrom,
    useReviewContext,
} from './ContextSection';
import { DownloadRow } from '../AgentTaskCard';
import { ExportToSharePointButton } from '../ExportToSharePointButton';
import RejectionDialog from '../RejectionDialog';
import ScrivenerWorkbench from '../composition-workbench/scrivener';
import HeraldWorkbench from '../composition-workbench/herald';
import EmissaryWorkbench from '../composition-workbench/emissary';
import CharterWorkbench from '../composition-workbench/charter';
import HavenWorkbench from '../composition-workbench/haven';
import ForumWorkbench from '../composition-workbench/forum';
import ArbiterWorkbench from '../composition-workbench/arbiter';

type ReviewStatus = 'pending' | 'approved' | 'rejected';

interface ArtefactRow {
    id: string;
    callId: string;
    clientId: string | null;
    reviewStatus?: ReviewStatus | null;
}

interface FactRow extends ArtefactRow {
    category: string;
    text: string;
    source_quote: string;
    source_timestamp: string;
    source_line_id?: string | null;
    confidence: 'high' | 'medium' | 'low';
    compliance_relevant: boolean;
    compliance_reason: string | null;
}

interface ActionRow extends ArtefactRow {
    title: string;
    owner: string;
    due_description: string;
    note: string | null;
    source_line_id?: string | null;
}

interface NoteRow extends ArtefactRow {
    title: string;
    body: string;
    tags: string[];
}

interface NarrativeDraftRow {
    id: string;
    clientId: string;
    period: string;
    selectedVariantIndex: number;
    performanceSummary: string;
    narrativeParagraph: string;
    variants: Array<{
        tone: string;
        performanceSummary: string;
        narrativeParagraph: string;
    }>;
    kbNotesCited: string[];
    reviewStatus?: ReviewStatus | null;
}

// -------- Call drafts body --------
//
// Staging review: each fact / action / note is a tri-state — accept, reject, or
// leave undecided (the default). The thumbs only STAGE a decision; nothing is
// committed until "Confirm". Undecided rows are simply omitted from the batch,
// so they stay pending in the queue. One batch request commits the lot.

type StageDecision = 'approve' | 'reject';
interface Staged {
    kind: ReviewableKind;
    decision: StageDecision;
    reason?: string;
}

export function CallDraftsBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: CallDraftsReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    const useAgentRows = <T,>(table: AgentRowTable, key: string) =>
        useQuery<T[]>({
            queryKey: ['agent', 'rows', key],
            queryFn: async () => {
                const res = await api.agent.listAgentRows({
                    params: { table },
                    query: {},
                });
                if (res.status !== 200) throw new Error(`fetch ${res.status}`);
                return res.body.rows as unknown as T[];
            },
            refetchInterval: 30_000,
        });
    const allFacts =
        useAgentRows<FactRow>('call_extracted_facts', 'call_extracted_facts').data ?? [];
    const allActions = useAgentRows<ActionRow>('call_action_items', 'call_action_items').data ?? [];
    const allNotes = useAgentRows<NoteRow>('call_kb_notes', 'call_kb_notes').data ?? [];
    const { pushToast } = useToast();
    const queryClient = useQueryClient();

    // Live call context (header + transcript) — which call, when, who. The
    // transcript drives the per-fact "show in transcript" expander.
    const contextBlocks = useReviewContext('call', item.callId);
    const headerBlocks = contextBlocks.filter((b) => b.type !== 'transcriptExcerpt');
    const transcript = transcriptFrom(contextBlocks);

    // Staged decisions keyed by artefact id (ids are unique across the three
    // call tables). Undecided rows are absent from the map.
    const [staged, setStaged] = useState<Record<string, Staged>>({});
    const [busy, setBusy] = useState(false);

    const stage = (id: string, kind: ReviewableKind, decision: StageDecision | undefined): void => {
        setStaged((prev) => {
            if (decision === undefined) {
                if (!(id in prev)) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            }
            return { ...prev, [id]: { ...prev[id], kind, decision } };
        });
    };
    const stageReason = (id: string, reason: string): void => {
        setStaged((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], reason } } : prev));
    };

    const include = (status: ReviewStatus | null | undefined): boolean => {
        if (showReviewed) return true;
        return status == null || status === 'pending';
    };

    const facts = useMemo(
        () => allFacts.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allFacts, item.callId, showReviewed],
    );
    const actions = useMemo(
        () => allActions.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allActions, item.callId, showReviewed],
    );
    const notes = useMemo(
        () => allNotes.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allNotes, item.callId, showReviewed],
    );

    const pendingRows: Array<{ id: string; kind: ReviewableKind }> = [
        ...facts
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'call-extracted-fact' as const })),
        ...actions
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'call-action-item' as const })),
        ...notes
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'call-kb-note' as const })),
    ];
    const pendingTotal = pendingRows.length;
    const stagedEntries = Object.entries(staged);
    const stagedCount = stagedEntries.length;
    const undecided = pendingTotal - stagedCount;

    // Stage every still-undecided pending row as accept (the bulk-happy path).
    // The reviewer can then thumb-down the few bad ones before confirming.
    function acceptAll(): void {
        setStaged((prev) => {
            const next = { ...prev };
            for (const r of pendingRows) {
                if (!(r.id in next)) next[r.id] = { kind: r.kind, decision: 'approve' };
            }
            return next;
        });
    }

    async function confirmDecisions(): Promise<void> {
        const decisions: ReviewDecision[] = stagedEntries.map(([id, s]) => ({
            target: 'artefact',
            kind: s.kind,
            id,
            decision: s.decision,
            reason: s.reason,
        }));
        if (decisions.length === 0) return;
        setBusy(true);
        const batch = await submitReviewDecisions(decisions);
        setBusy(false);
        if (!batch.ok) {
            pushToast({ kind: 'error', message: batch.error ?? 'Failed to confirm decisions' });
            return;
        }
        const { confirmed, failed } = batch.summary;
        pushToast(
            failed > 0
                ? { kind: 'info', message: `Confirmed ${confirmed}, ${failed} failed` }
                : {
                      kind: 'success',
                      message: `Confirmed ${confirmed} decision${confirmed === 1 ? '' : 's'}`,
                  },
        );
        void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
        void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
        setStaged({});
        onCommitted?.();
    }

    return (
        <div className="flex flex-col gap-4">
            {headerBlocks.length > 0 && <ContextBlocks blocks={headerBlocks} />}

            {pendingTotal > 0 && stagedCount === 0 && (
                <div className="flex items-center justify-end">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={acceptAll}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Accept all ({pendingTotal})
                    </Button>
                </div>
            )}

            {facts.length === 0 && actions.length === 0 && notes.length === 0 && (
                <EmptyState title="Nothing left to review for this call." />
            )}

            {facts.length > 0 && (
                <SectionShell title="Extracted facts" count={facts.length}>
                    {facts.map((f) => (
                        <FactItem
                            key={f.id}
                            row={f}
                            staged={staged[f.id]?.decision}
                            reason={staged[f.id]?.reason ?? ''}
                            transcript={transcript}
                            onStage={(d) => stage(f.id, 'call-extracted-fact', d)}
                            onStageReason={(r) => stageReason(f.id, r)}
                        />
                    ))}
                </SectionShell>
            )}

            {actions.length > 0 && (
                <SectionShell title="Action items" count={actions.length}>
                    {actions.map((a) => (
                        <ActionItem
                            key={a.id}
                            row={a}
                            staged={staged[a.id]?.decision}
                            reason={staged[a.id]?.reason ?? ''}
                            transcript={transcript}
                            onStage={(d) => stage(a.id, 'call-action-item', d)}
                            onStageReason={(r) => stageReason(a.id, r)}
                        />
                    ))}
                </SectionShell>
            )}

            {notes.length > 0 && (
                <SectionShell title="KB notes" count={notes.length}>
                    {notes.map((n) => (
                        <NoteItem
                            key={n.id}
                            row={n}
                            staged={staged[n.id]?.decision}
                            reason={staged[n.id]?.reason ?? ''}
                            onStage={(d) => stage(n.id, 'call-kb-note', d)}
                            onStageReason={(r) => stageReason(n.id, r)}
                        />
                    ))}
                </SectionShell>
            )}

            {stagedCount > 0 && (
                // Full-bleed sticky footer, pinned flush to the scrollport
                // bottom. `-mx-card-padding` cancels the container's horizontal
                // padding; the container drops its *bottom* padding for this body
                // (ReviewItemPanel) so a plain `bottom-0` sits flush with no gap
                // and no scroll wobble (a negative offset would drift on scroll).
                <div className="sticky bottom-0 -mx-card-padding px-card-padding py-3 flex items-center justify-between gap-3 bg-[rgb(var(--color-page))] border-t border-[rgb(var(--color-divider))]">
                    <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                        {stagedCount} staged
                        {undecided > 0 ? ` · ${undecided} left pending` : ''}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setStaged({})}
                            disabled={busy}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => void confirmDecisions()}
                            disabled={busy}
                            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                        >
                            Confirm {stagedCount} decision{stagedCount === 1 ? '' : 's'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionShell({
    title,
    count,
    children,
}: {
    title: string;
    count: number;
    children: React.ReactNode;
}) {
    return (
        <section className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <h3 className="text-card-title text-[rgb(var(--color-text-primary))]">{title}</h3>
                <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                    {String(count).padStart(2, '0')}
                </span>
            </div>
            <ul className="flex flex-col gap-2">{children}</ul>
        </section>
    );
}

function StatusBadge({ status }: { status: ReviewStatus | null | undefined }) {
    if (status === 'approved') {
        return <Pill variant="green" size="sm" label="approved" />;
    }
    if (status === 'rejected') {
        return <Pill variant="red" size="sm" label="rejected" />;
    }
    return <Pill variant="amber" size="sm" label="pending" />;
}

interface StagingRowProps {
    status: ReviewStatus | null | undefined;
    staged: StageDecision | undefined;
    onStage: (decision: StageDecision | undefined) => void;
}

// Tri-state thumbs: highlighted when staged; toggling the active thumb clears
// the staged decision (back to undecided). Reviewed rows render nothing here
// (their StatusBadge carries the disposition).
function StagingControls({ status, staged, onStage }: StagingRowProps) {
    if (status != null && status !== 'pending') return null;
    return (
        <div className="flex items-center gap-1">
            <Button
                variant={staged === 'approve' ? 'primary' : 'ghost'}
                size="sm"
                aria-pressed={staged === 'approve'}
                aria-label="Accept"
                title="Accept"
                onClick={() => onStage(staged === 'approve' ? undefined : 'approve')}
                icon={<ThumbsUp className="w-3.5 h-3.5" />}
            />
            <Button
                variant={staged === 'reject' ? 'danger' : 'ghost'}
                size="sm"
                aria-pressed={staged === 'reject'}
                aria-label="Reject"
                title="Reject"
                onClick={() => onStage(staged === 'reject' ? undefined : 'reject')}
                icon={<ThumbsDown className="w-3.5 h-3.5" />}
            />
        </div>
    );
}

// Optional inline reason, shown only when a row is staged for rejection (the
// server defaults an empty reason, so it stays optional).
function RejectReasonField({
    reason,
    onChange,
}: {
    reason: string;
    onChange: (r: string) => void;
}) {
    return (
        <input
            type="text"
            value={reason}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Why reject? (optional)"
            className={cn(
                'w-full rounded-button border border-[rgb(var(--color-divider))] bg-transparent',
                'px-2 py-1 text-annotation text-[rgb(var(--color-text-primary))]',
                'placeholder:text-[rgb(var(--color-text-tertiary))]',
                'focus:outline-none focus:border-[rgb(var(--color-accent))]',
            )}
        />
    );
}

// Border tint reflecting the staged decision so the row reads at a glance.
function stagedBorder(staged: StageDecision | undefined): string {
    if (staged === 'approve') return 'border-[rgb(var(--color-accent))]';
    if (staged === 'reject') return 'border-[rgb(var(--color-rag-red))]';
    return 'border-[rgb(var(--color-divider))]';
}

interface StagedItemProps<R> {
    row: R;
    staged: StageDecision | undefined;
    reason: string;
    /** Call transcript lines (call drafts) for the cited-line expander. */
    transcript?: ContextTranscriptLine[] | null;
    onStage: (decision: StageDecision | undefined) => void;
    onStageReason: (reason: string) => void;
}

function FactItem({
    row,
    staged,
    reason,
    transcript,
    onStage,
    onStageReason,
}: StagedItemProps<FactRow>) {
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1.5',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <Pill variant="grey" size="sm" label={row.category} />
                <Pill
                    variant={confidenceVariant(row.confidence)}
                    size="sm"
                    label={`${row.confidence} confidence`}
                />
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.text}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            {row.source_quote && (
                <div className="flex items-start gap-1.5 text-annotation italic text-[rgb(var(--color-text-secondary))]">
                    <Quote className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.source_quote}</span>
                </div>
            )}
            {row.compliance_relevant && (
                <div className="flex items-start gap-1.5 text-annotation text-[rgb(var(--color-rag-amber))]">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.compliance_reason ?? 'Compliance-relevant'}</span>
                </div>
            )}
            <CitedTranscriptExpander lines={transcript ?? null} lineId={row.source_line_id} />
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
        </li>
    );
}

function confidenceVariant(c: 'high' | 'medium' | 'low'): 'green' | 'amber' | 'red' {
    return c === 'high' ? 'green' : c === 'medium' ? 'amber' : 'red';
}

function ActionItem({
    row,
    staged,
    reason,
    transcript,
    onStage,
    onStageReason,
}: StagedItemProps<ActionRow>) {
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.title}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                {row.owner} · {row.due_description}
                {row.note ? ` · ${row.note}` : ''}
            </div>
            <CitedTranscriptExpander lines={transcript ?? null} lineId={row.source_line_id} />
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
        </li>
    );
}

function NoteItem({ row, staged, reason, onStage, onStageReason }: StagedItemProps<NoteRow>) {
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1.5',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <span className="flex-1 text-body-medium font-medium text-[rgb(var(--color-text-primary))]">
                    {row.title}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            <CitedSummary body={row.body} callId={row.callId} className="text-annotation" />
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
        </li>
    );
}

// -------- Monthly narrative body --------

export function MonthlyNarrativeBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: MonthlyNarrativeReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    const { data: draftsData } = useQuery<NarrativeDraftRow[]>({
        queryKey: ['agent', 'rows', 'wealth_monthly_narrative_drafts'],
        queryFn: async () => {
            const res = await api.agent.listAgentRows({
                params: { table: 'wealth_monthly_narrative_drafts' },
                query: {},
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.rows as unknown as NarrativeDraftRow[];
        },
        refetchInterval: 30_000,
    });
    const allDrafts = draftsData ?? [];
    const { pushToast } = useToast();
    const queryClient = useQueryClient();
    const draft = useMemo(
        () => allDrafts.find((d) => d.id === item.draftId),
        [allDrafts, item.draftId],
    );
    const [activeTone, setActiveTone] = useState<string | null>(null);
    const [rejecting, setRejecting] = useState(false);
    const [requestingRevision, setRequestingRevision] = useState(false);

    if (!draft) {
        return (
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Draft data unavailable — it may have just been removed.
            </div>
        );
    }

    if (!showReviewed && draft.reviewStatus && draft.reviewStatus !== 'pending') {
        return (
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))] italic">
                Reviewed. Toggle "Show reviewed" above to see decided drafts.
            </div>
        );
    }

    const currentTone =
        activeTone ??
        draft.variants[draft.selectedVariantIndex]?.tone ??
        draft.variants[0]?.tone ??
        'plain';
    const variant = draft.variants.find((v) => v.tone === currentTone) ?? draft.variants[0];

    async function handleApprove(): Promise<void> {
        if (!variant) return;
        const idx = draft!.variants.findIndex((v) => v.tone === currentTone);
        const result = await approveReviewable('monthly-narrative-draft', draft!.id, {
            selectedVariantIndex: idx >= 0 ? idx : 0,
            performanceSummary: variant.performanceSummary,
            narrativeParagraph: variant.narrativeParagraph,
        });
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Narrative approved' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        }
    }

    async function handleReject(reason: string): Promise<void> {
        const result = await rejectReviewable(
            'monthly-narrative-draft',
            draft!.id,
            reason || undefined,
        );
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Narrative rejected' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        }
    }

    async function handleRequestRevision(reason: string): Promise<void> {
        const result = await requestRevisionReviewable(
            'monthly-narrative-draft',
            draft!.id,
            reason || 'No reason given',
        );
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Revision requested — ATLAS will re-roll' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        }
    }

    return (
        <div className="flex flex-col gap-3">
            <ContextSection subjectKind="monthly-narrative-draft" id={item.draftId} />
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={draft.reviewStatus} />
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {draft.kbNotesCited.length} KB note
                    {draft.kbNotesCited.length === 1 ? '' : 's'} cited
                </span>
            </div>
            <div className="flex gap-1 border-b border-[rgb(var(--color-divider))]">
                {draft.variants.map((v) => (
                    <button
                        key={v.tone}
                        type="button"
                        onClick={() => setActiveTone(v.tone)}
                        className={cn(
                            'px-3 py-1.5 text-annotation font-medium border-b-2 -mb-px',
                            currentTone === v.tone
                                ? 'border-[rgb(var(--color-accent))] text-[rgb(var(--color-accent))]'
                                : 'border-transparent text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))]',
                        )}
                    >
                        {v.tone}
                    </button>
                ))}
            </div>
            {variant && (
                <div className="flex flex-col gap-2">
                    <div className="text-body-medium font-medium text-[rgb(var(--color-text-primary))]">
                        {variant.performanceSummary}
                    </div>
                    <div className="text-body-medium text-[rgb(var(--color-text-secondary))] whitespace-pre-wrap">
                        {variant.narrativeParagraph}
                    </div>
                </div>
            )}
            {(draft.reviewStatus == null || draft.reviewStatus === 'pending') && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRejecting(true)}
                        icon={<ThumbsDown className="w-3.5 h-3.5" />}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setRequestingRevision(true)}
                        icon={<RotateCcw className="w-3.5 h-3.5" />}
                    >
                        Request revision
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleApprove()}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Approve {currentTone}
                    </Button>
                </div>
            )}
            <RejectionDialog
                open={rejecting}
                title="Reject narrative?"
                subject={`${item.primaryLabel} · ${item.period}`}
                placeholder="Optional — note why so ATLAS can re-roll."
                onConfirm={(reason) => void handleReject(reason)}
                onClose={() => setRejecting(false)}
            />
            <RejectionDialog
                open={requestingRevision}
                tone="neutral"
                title="Request revision?"
                subject={`${item.primaryLabel} · ${item.period}`}
                placeholder="Tell ATLAS what to change so it can re-roll the narrative."
                confirmLabel="Request revision"
                onConfirm={(reason) => void handleRequestRevision(reason)}
                onClose={() => setRequestingRevision(false)}
            />
        </div>
    );
}

// -------- CEDAR coac-action-draft body (ADR-0016, editable correction) --------
//
// Unlike every other detector-style finding, CoacActionDraft's approval flow
// lets Ops correct the extracted fields before they materialise a live
// CoacAction (mirrors the retired bespoke ApproveCoacActionDraft action's
// override inputs). Plain controlled inputs, not a full form library —
// `coacActionDraftExtraPatch` on the server applies whichever of these fields
// changed before the reviewStatus flip + materialise.

function Field({
    label,
    value,
    onChange,
    disabled,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    disabled: boolean;
    placeholder?: string;
}) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">{label}</span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                className={cn(
                    'w-full rounded-button border border-[rgb(var(--color-divider))] bg-transparent',
                    'px-2.5 py-1.5 text-body-medium text-[rgb(var(--color-text-primary))]',
                    'placeholder:text-[rgb(var(--color-text-tertiary))]',
                    'focus:outline-none focus:border-[rgb(var(--color-accent))]',
                    'disabled:opacity-60',
                )}
            />
        </label>
    );
}

export function CoacActionDraftBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: CoacActionDraftReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    const { data: draftsData } = useQuery<
        Array<{
            id: string;
            reviewStatus?: ReviewStatus | null;
            sourceReferenceId: string;
            narrative: string | null;
        }>
    >({
        queryKey: ['agent', 'rows', 'coac_action_drafts'],
        queryFn: async () => {
            const res = await api.agent.listAgentRows({
                params: { table: 'coac_action_drafts' },
                query: {},
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.rows as unknown as Array<{
                id: string;
                reviewStatus?: ReviewStatus | null;
                sourceReferenceId: string;
                narrative: string | null;
            }>;
        },
        refetchInterval: 30_000,
    });
    const draft = useMemo(
        () => draftsData?.find((d) => d.id === item.draftId),
        [draftsData, item.draftId],
    );
    const { pushToast } = useToast();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(false);

    const [securityName, setSecurityName] = useState(item.securityName);
    const [eventType, setEventType] = useState(item.eventType);
    const [responseDeadline, setResponseDeadline] = useState(item.responseDeadline ?? '');
    const [wealthManager, setWealthManager] = useState(item.wealthManager ?? '');
    const [correctionNote, setCorrectionNote] = useState('');

    const status = draft?.reviewStatus ?? 'pending';
    if (!showReviewed && status !== 'pending') {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                This draft has been reviewed.
            </p>
        );
    }

    async function handleApprove(): Promise<void> {
        setBusy(true);
        const result = await approveReviewable('coac-action-draft', item.draftId, {
            securityName,
            eventType,
            responseDeadline: responseDeadline || null,
            wealthManager: wealthManager || null,
            ...(correctionNote ? { correctionNote } : {}),
        });
        setBusy(false);
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Corporate action approved' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        } else {
            pushToast({ kind: 'error', message: result.error ?? 'Approve failed' });
        }
    }

    async function handleReject(reason: string): Promise<void> {
        setBusy(true);
        const result = await rejectReviewable(
            'coac-action-draft',
            item.draftId,
            reason || undefined,
        );
        setBusy(false);
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Draft rejected' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        } else {
            pushToast({ kind: 'error', message: result.error ?? 'Reject failed' });
        }
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={status} />
                <span className="text-annotation font-mono text-[rgb(var(--color-text-tertiary))]">
                    ref {draft?.sourceReferenceId ?? item.sourceReferenceId}
                </span>
                {item.securityId && (
                    <span className="text-annotation font-mono text-[rgb(var(--color-text-tertiary))]">
                        {item.securityId}
                    </span>
                )}
            </div>

            {(draft?.narrative ?? item.narrative) && (
                <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug whitespace-pre-wrap">
                    {draft?.narrative ?? item.narrative}
                </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                    label="Security name"
                    value={securityName}
                    onChange={setSecurityName}
                    disabled={busy || status !== 'pending'}
                />
                <Field
                    label="Event type"
                    value={eventType}
                    onChange={setEventType}
                    disabled={busy || status !== 'pending'}
                />
                <Field
                    label="Response deadline"
                    value={responseDeadline}
                    onChange={setResponseDeadline}
                    disabled={busy || status !== 'pending'}
                    placeholder="none"
                />
                <Field
                    label="Wealth manager"
                    value={wealthManager}
                    onChange={setWealthManager}
                    disabled={busy || status !== 'pending'}
                    placeholder="none"
                />
            </div>

            {status === 'pending' && (
                <Field
                    label="Correction note (optional)"
                    value={correctionNote}
                    onChange={setCorrectionNote}
                    disabled={busy}
                    placeholder="What did you change and why?"
                />
            )}

            {status === 'pending' && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRejecting(true)}
                        disabled={busy}
                        icon={<ThumbsDown className="w-3.5 h-3.5" />}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleApprove()}
                        disabled={busy}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Approve
                    </Button>
                </div>
            )}

            <RejectionDialog
                open={rejecting}
                title="Reject corporate action draft?"
                subject={item.primaryLabel}
                placeholder="Optional — note why so Ops knows not to re-upload as-is."
                onConfirm={(reason) => void handleReject(reason)}
                onClose={() => setRejecting(false)}
            />
        </div>
    );
}

// -------- Composer draft body (generic <CompositionWorkbench>) --------
//
// Renders the per-agent composition-workbench wrapper for a pending
// `*Draft` row. The draft row is re-fetched here by id from its table so
// the workbench gets the full envelope (brief / sources / plan / variants
// / handoff / citations). Approve / reject / request-revision mutations are
// owned by the generic <CompositionWorkbench>.

export function ComposerDraftBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: ComposerDraftReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    // `item.table` is `AgentRowTable` (contract allow-list) — fetching a
    // non-allow-listed (e.g. finding) table here is a compile error.
    const table: AgentRowTable = item.table;
    const { data: rowsData } = useQuery<BaseDraft[]>({
        queryKey: ['agent', 'rows', table],
        queryFn: async () => {
            const res = await api.agent.listAgentRows({
                params: { table },
                query: {},
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.rows as unknown as BaseDraft[];
        },
        refetchInterval: 30_000,
    });
    const rows = rowsData ?? [];
    const draft = useMemo(
        () => rows.find((d) => d.id === item.draftId) ?? null,
        [rows, item.draftId],
    );

    if (!draft) {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Draft no longer available.
            </p>
        );
    }
    if (!showReviewed && draft.reviewStatus && draft.reviewStatus !== 'pending') {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                This draft has been reviewed.
            </p>
        );
    }

    // Close the panel on any committed decision (approve / reject /
    // request-revision) — the workbench owns the mutation + toast.
    const hooks = {
        onApproved: onCommitted,
        onRejected: onCommitted,
        onRevisionRequested: onCommitted,
    };
    const workbench = (() => {
        switch (item.agent) {
            case 'SCRIVENER':
                return <ScrivenerWorkbench draft={draft} {...hooks} />;
            case 'HERALD':
                return <HeraldWorkbench draft={draft} {...hooks} />;
            case 'EMISSARY':
                return <EmissaryWorkbench draft={draft} {...hooks} />;
            case 'CHARTER':
                return <CharterWorkbench draft={draft} {...hooks} />;
            case 'HAVEN':
                return <HavenWorkbench draft={draft} {...hooks} />;
            case 'FORUM':
                return <ForumWorkbench draft={draft} {...hooks} />;
            case 'ARBITER':
                return <ArbiterWorkbench draft={draft} {...hooks} />;
        }
    })();
    return (
        <div className="flex flex-col gap-3">
            <ContextSection subjectKind={COMPOSER_ARTEFACT_KIND[item.agent]} id={item.draftId} />
            {workbench}
        </div>
    );
}

// The composer agent → its artefact kind (the context subject key).
const COMPOSER_ARTEFACT_KIND: Record<ComposerDraftReviewItem['agent'], string> = {
    SCRIVENER: 'suitability-letter-draft',
    HERALD: 'onboarding-pack-draft',
    EMISSARY: 'client-communication-draft',
    CHARTER: 'fund-report-draft',
    HAVEN: 'vulnerability-adaptation-plan-draft',
    FORUM: 'governance-paper-draft',
    ARBITER: 'fair-value-assessment-draft',
};

// -------- Task body (generic AgentTask review_required) --------

export function TaskBody({
    item,
    onCommitted,
}: {
    item: TaskReviewItem;
    onCommitted?: () => void;
}) {
    const { reviewTask } = useAgent();
    const task = item.task;
    const summary = describeTaskOutput(task);
    const [rejecting, setRejecting] = useState(false);

    const decide = (action: 'approve' | 'reject', reason?: string): void => {
        void reviewTask(task.id, action, reason).then((ok) => {
            if (ok) onCommitted?.();
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status="pending" />
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {item.secondaryLabel}
                </span>
            </div>
            {task.artefactType && task.artefactId && (
                <ContextSection subjectKind={task.artefactType} id={task.artefactId} />
            )}
            {summary && (
                <p className="text-body-medium text-[rgb(var(--color-text-secondary))] leading-snug">
                    {summary}
                </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
                <DownloadRow task={task} />
                <ExportToSharePointButton task={task} />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                <Link
                    to={item.contextHref}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-annotation rounded-button',
                        'border border-[rgb(var(--color-divider))]',
                        'text-[rgb(var(--color-text-primary))]',
                        'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                        'transition-colors duration-card-hover ease-swiss',
                    )}
                >
                    <FileText className="w-3.5 h-3.5" />
                    Open full draft
                </Link>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRejecting(true)}
                    icon={<XCircle className="w-3.5 h-3.5" />}
                >
                    Reject
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => decide('approve')}
                    icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                >
                    Approve
                </Button>
            </div>
            <RejectionDialog
                open={rejecting}
                subject={`${item.primaryLabel} · ${item.secondaryLabel}`}
                onConfirm={(reason) => decide('reject', reason || undefined)}
                onClose={() => setRejecting(false)}
            />
        </div>
    );
}

// One-line summary of a task's result, derived from its primary produced
// artefact row (PRD #261 follow-up — the legacy `output` union is no longer
// populated by compose). `summariseArtefact` reads the shared content fields
// (summary / headline / subject / claim / variant body) defensively.
export function describeTaskOutput(task: AgentTask): string | null {
    const row = task.artefacts?.[0]?.row;
    return row ? summariseArtefact(row) : null;
}
